import { Box, Chip, Tooltip } from '@mui/material';
import type { Company, Group, User } from '@esg/esg-global-types';

import type { CompanyExtended } from '../../lib/app/company';
import {
  checkUserGroupAdmin,
  getAdminGroups,
  getUserCompanies,
  userAllowedCompany
} from '../../util/user_access';

interface CompanyChipStackProps {
  /** The row user whose access this cell renders. */
  user: User;
  /** Unscoped source-of-truth list, used to resolve names from ids. */
  groups: Group[];
  /**
   * The signed-in viewer. A non-super viewer only sees chips for companies and
   * groups within their own admin scope, even when the row user has more.
   */
  current_user: User | null;
  /** Max chips rendered inline; the rest collapse into a "+N" overflow chip. */
  max_visible?: number;
}

const DEFAULT_MAX_VISIBLE = 2;

/** Companies column cell renderer — inline chips with a "+N" overflow chip. */
const CompanyChipStack = ({
  user,
  groups,
  current_user,
  max_visible = DEFAULT_MAX_VISIBLE
}: CompanyChipStackProps) => {
  if (user.super_admin) {
    return <Chip size="small" label="All companies" variant="outlined" />;
  }

  // Super-admin viewers (and the defensive no-viewer case) see every chip; a
  // group admin is scoped to the groups / companies they administer.
  const viewerIsSuper = !current_user || !!current_user.super_admin;

  // id → its Company and owning Group, built from the unscoped list so in-scope
  // names still resolve even when the toolbar list is scoped to the viewer.
  const companyLookup = new Map<string, { company: Company; group: Group }>();
  for (const group of groups) {
    for (const company of group.companies ?? []) {
      companyLookup.set(company.id, { company, group });
    }
  }

  const userCompanies: Array<{ company: Company; group: Group }> = getUserCompanies(user)
    .map((id) => companyLookup.get(id))
    .filter((entry): entry is { company: Company; group: Group } => entry !== undefined);

  const visibleCompanies: Company[] = userCompanies
    .filter(({ company, group }) => {
      if (viewerIsSuper) return true;
      const companyExtended: CompanyExtended = { ...company, group };
      return userAllowedCompany(current_user as User, companyExtended);
    })
    .map(({ company }) => company);

  const adminGroups: Group[] = getAdminGroups(user)
    .map((id) => groups.find((g) => g.id === id))
    .filter((g): g is Group => g !== undefined)
    .filter((g) => viewerIsSuper || checkUserGroupAdmin(current_user as User, g.id));

  const chipItems: Array<{ label: string; tone: 'admin' | 'company' }> = [
    ...adminGroups.map((g) => ({ label: `Group: ${g.name}`, tone: 'admin' as const })),
    ...visibleCompanies.map((c) => ({ label: c.name, tone: 'company' as const }))
  ];

  if (chipItems.length === 0) {
    return (
      <Chip size="small" label="No access" variant="outlined" sx={{ color: 'text.disabled' }} />
    );
  }

  const visible = chipItems.slice(0, max_visible);
  const overflow = chipItems.slice(max_visible);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {visible.map((item) => (
        <Chip
          key={item.label}
          size="small"
          label={item.label}
          variant={item.tone === 'admin' ? 'filled' : 'outlined'}
          color={item.tone === 'admin' ? 'primary' : 'default'}
        />
      ))}
      {overflow.length > 0 && (
        <Tooltip title={overflow.map((i) => i.label).join(', ')} arrow>
          <Chip size="small" label={`+${overflow.length}`} variant="outlined" />
        </Tooltip>
      )}
    </Box>
  );
};

export default CompanyChipStack;
